import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useCompany } from "../../contexts/CompanyContext";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import { getProviderList } from "../../api/providers";
import { getApFlow } from "../../api/accountsPayable";
import { handleError } from "../../services/errorHandler";
import { formatCurrency } from "../../services/valueFormatters";
import ApSankeyChart, { LAYER_LABELS } from "../../molecules/AccountsPayable/ApSankeyChart";

const DEFAULT_LAYERS = ["po", "ra", "rapayment", "sp", "tx", "payment"];

const MATCH_LABELS = {
  po: "Orden de compra",
  ra: "Recepción",
  rapayment: "Parcialidad",
  sp: "Pago programado",
  tx: "Transacción bancaria",
  payment: "Pago (esquema anterior)",
};

export default function ApFlowScreen() {
  const { selectedCompany } = useCompany();
  const [provider, setProvider] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [codeInput, setCodeInput] = useState("");
  const [code, setCode] = useState("");
  const [layers, setLayers] = useState(DEFAULT_LAYERS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const companyId = selectedCompany?.id;
  const previousCompanyId = useRef(companyId);

  const hasFilter = Boolean(code || provider?.code);

  const fetchFlow = useCallback(async () => {
    if (!companyId || !hasFilter) {
      setData(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getApFlow({
        companyId,
        // A code trace resolves its own provider and date span, so those filters are left out entirely.
        providerCode: code ? undefined : provider?.code,
        code: code || undefined,
        startDate: code ? undefined : startDate?.format?.("YYYY-MM-DD") ?? undefined,
        endDate: code ? undefined : endDate?.format?.("YYYY-MM-DD") ?? undefined,
        layers,
      });
      setData(response.data);
    } catch (err) {
      setData(null);
      setError(handleError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, hasFilter, code, provider?.code, startDate, endDate, layers]);

  useEffect(() => {
    if (previousCompanyId.current !== companyId) {
      previousCompanyId.current = companyId;
      setProvider(null);
      setCode("");
      setCodeInput("");
      setData(null);
      setError(null);
      return;
    }

    fetchFlow();
  }, [companyId, fetchFlow]);

  const submitCode = () => setCode(codeInput.trim());

  const clearCode = () => {
    setCodeInput("");
    setCode("");
  };

  const traceCode = (nextCode) => {
    setCodeInput(nextCode);
    setCode(nextCode);
  };

  const handleLayersChange = (_, nextLayers) =>
    setLayers(nextLayers.length > 0 ? nextLayers : DEFAULT_LAYERS);

  if (!selectedCompany) {
    return (
      <DashboardLayout>
        <Box p={4}>
          <Typography>Seleccione una compañía primero</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  const match = data?.match;
  const totals = data?.totals;
  const warnings = data?.warnings ?? [];

  return (
    <DashboardLayout>
      <Box p={4} sx={{ height: "calc(100vh - 64px)", display: "flex", flexDirection: "column" }}>
        <Typography variant="h4" component="h1" sx={{ textTransform: "uppercase", mb: 2 }}>
          Flujo de cuentas por pagar
        </Typography>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          <TextField
            label="Buscar por código"
            placeholder="OC, recepción, pago programado o pago"
            value={codeInput}
            onChange={(event) => setCodeInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") submitCode();
            }}
            sx={{ minWidth: 320 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {code && (
                    <IconButton size="small" onClick={clearCode} aria-label="Limpiar código">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={submitCode} aria-label="Buscar código">
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ minWidth: 260 }}>
            <LGAutocomplete
              fetchFunction={getProviderList}
              queryParams={{ companyId }}
              optionLabel="code"
              value={provider}
              onChange={(_, value) => setProvider(value)}
              label="Proveedor"
              disabled={Boolean(code)}
            />
          </Box>
          <MyDatePicker
            label="Fecha inicial"
            value={startDate}
            onChange={(value) => setStartDate(value ? dayjs(value) : null)}
            disabled={Boolean(code)}
          />
          <MyDatePicker
            label="Fecha final"
            value={endDate}
            onChange={(value) => setEndDate(value ? dayjs(value) : null)}
            disabled={Boolean(code)}
          />
          <Button variant="contained" onClick={fetchFlow} disabled={!hasFilter || loading}>
            Actualizar
          </Button>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <ToggleButtonGroup value={layers} onChange={handleLayersChange} size="small">
            {Object.entries(LAYER_LABELS).map(([layer, label]) => (
              <ToggleButton key={layer} value={layer} sx={{ textTransform: "none" }}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {totals && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" color="success" label={`Pagado ${formatCurrency(totals.paid)}`} />
              <Chip size="small" color="warning" label={`Por pagar ${formatCurrency(totals.unpaid)}`} />
              <Chip size="small" label={`Sin facturar ${formatCurrency(totals.uninvoiced)}`} />
            </Stack>
          )}
        </Stack>

        {match && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Tooltip title="Se muestra únicamente la cadena conectada a este documento">
              <Chip
                color="primary"
                onDelete={clearCode}
                label={`${MATCH_LABELS[match.layer] ?? "Documento"}: ${match.label ?? match.code}`}
              />
            </Tooltip>
            {match.providerCode && (
              <Typography variant="body2" color="text.secondary">
                Proveedor {match.providerCode}
                {match.providerName ? ` · ${match.providerName}` : ""}
              </Typography>
            )}
          </Stack>
        )}

        {warnings.map((warning) => (
          <Alert key={warning} severity="info" sx={{ mb: 1 }}>
            {warning}
          </Alert>
        ))}

        <Paper
          sx={{
            p: 2,
            flex: 1,
            minHeight: 480,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!hasFilter && (
            <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
              <Typography color="text.secondary">
                Seleccione un proveedor o busque un código para ver el flujo.
              </Typography>
            </Box>
          )}

          {hasFilter && loading && (
            <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
              <CircularProgress />
            </Box>
          )}

          {hasFilter && !loading && error && <Alert severity="error">{error}</Alert>}

          {hasFilter && !loading && !error && (
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ApSankeyChart
                data={data}
                highlightNodeIds={match?.nodeIds}
                onNodeSelect={traceCode}
              />
            </Box>
          )}
        </Paper>
      </Box>
    </DashboardLayout>
  );
}
